import type { FlagShape, GraphData, GraphEdge, GraphFlag, GraphNode } from "../types";

export type Op =
  | { t: "addNode"; n: GraphNode }
  | { t: "delNode"; n: GraphNode }
  | { t: "addEdge"; e: GraphEdge }
  | { t: "delEdge"; e: GraphEdge }
  | { t: "moveNode"; i: number; from: { x: number; y: number }; to: { x: number; y: number } }
  | { t: "setClass"; i: number; from: number; to: number }
  | { t: "addFlag"; f: GraphFlag }
  | { t: "delFlag"; f: GraphFlag }
  | { t: "setFlagText"; i: number; from: string; to: string };

const UNDO_LIMIT = 300;

export class Graph {
  nodes = new Map<number, GraphNode>();
  edges = new Map<number, GraphEdge>();
  flags = new Map<number, GraphFlag>(); // flagged spots with a note, not part of the network
  adj = new Map<number, Set<number>>(); // node id -> incident edge ids
  nextId = 1;
  rev = 0; // increments on every mutation batch; used for save bookkeeping

  private undoStack: Op[][] = [];
  private redoStack: Op[][] = [];

  load(data: GraphData): void {
    this.nodes.clear();
    this.edges.clear();
    this.flags.clear();
    this.adj.clear();
    for (const f of data.flags ?? []) this.flags.set(f.i, { ...f });
    for (const n of data.nodes) {
      this.nodes.set(n.i, { ...n });
      this.adj.set(n.i, new Set());
    }
    for (const e of data.edges) {
      this.edges.set(e.i, { ...e });
      this.adj.get(e.a)?.add(e.i);
      this.adj.get(e.b)?.add(e.i);
    }
    this.nextId = data.nextId;
    this.undoStack = [];
    this.redoStack = [];
  }

  serialize(): GraphData {
    return {
      nodes: [...this.nodes.values()].map((n) => ({ i: n.i, x: n.x, y: n.y })),
      edges: [...this.edges.values()].map((e) => ({ i: e.i, a: e.a, b: e.b, c: e.c })),
      nextId: this.nextId,
      flags: [...this.flags.values()].map((f) => ({ i: f.i, x: f.x, y: f.y, text: f.text, ...(f.shape ? { shape: f.shape } : {}) })),
    };
  }

  newId(): number {
    return this.nextId++;
  }

  degree(i: number): number {
    return this.adj.get(i)?.size ?? 0;
  }

  edgesOf(i: number): GraphEdge[] {
    const out: GraphEdge[] = [];
    for (const eid of this.adj.get(i) ?? []) {
      const e = this.edges.get(eid);
      if (e) out.push(e);
    }
    return out;
  }

  /** Is there already an edge connecting nodes a and b? */
  hasEdgeBetween(a: number, b: number): boolean {
    for (const e of this.edgesOf(a)) {
      if (e.a === b || e.b === b) return true;
    }
    return false;
  }

  applyOp(op: Op): void {
    switch (op.t) {
      case "addNode":
        this.nodes.set(op.n.i, { ...op.n });
        this.adj.set(op.n.i, new Set());
        break;
      case "delNode":
        this.nodes.delete(op.n.i);
        this.adj.delete(op.n.i);
        break;
      case "addEdge":
        this.edges.set(op.e.i, { ...op.e });
        this.adj.get(op.e.a)?.add(op.e.i);
        this.adj.get(op.e.b)?.add(op.e.i);
        break;
      case "delEdge": {
        this.edges.delete(op.e.i);
        this.adj.get(op.e.a)?.delete(op.e.i);
        this.adj.get(op.e.b)?.delete(op.e.i);
        break;
      }
      case "moveNode": {
        const n = this.nodes.get(op.i);
        if (n) {
          n.x = op.to.x;
          n.y = op.to.y;
        }
        break;
      }
      case "setClass": {
        const e = this.edges.get(op.i);
        if (e) e.c = op.to;
        break;
      }
      case "addFlag":
        this.flags.set(op.f.i, { ...op.f });
        break;
      case "delFlag":
        this.flags.delete(op.f.i);
        break;
      case "setFlagText": {
        const f = this.flags.get(op.i);
        if (f) f.text = op.to;
        break;
      }
    }
  }

  private invertOp(op: Op): Op {
    switch (op.t) {
      case "addNode":
        return { t: "delNode", n: op.n };
      case "delNode":
        return { t: "addNode", n: op.n };
      case "addEdge":
        return { t: "delEdge", e: op.e };
      case "delEdge":
        return { t: "addEdge", e: op.e };
      case "moveNode":
        return { t: "moveNode", i: op.i, from: op.to, to: op.from };
      case "setClass":
        return { t: "setClass", i: op.i, from: op.to, to: op.from };
      case "addFlag":
        return { t: "delFlag", f: op.f };
      case "delFlag":
        return { t: "addFlag", f: op.f };
      case "setFlagText":
        return { t: "setFlagText", i: op.i, from: op.to, to: op.from };
    }
  }

  /** Record a batch of already-applied ops as one undoable step. */
  commit(ops: Op[]): void {
    if (ops.length === 0) return;
    this.undoStack.push(ops);
    if (this.undoStack.length > UNDO_LIMIT) this.undoStack.shift();
    this.redoStack = [];
    this.rev++;
  }

  undo(): boolean {
    const ops = this.undoStack.pop();
    if (!ops) return false;
    for (let i = ops.length - 1; i >= 0; i--) this.applyOp(this.invertOp(ops[i]));
    this.redoStack.push(ops);
    this.rev++;
    return true;
  }

  redo(): boolean {
    const ops = this.redoStack.pop();
    if (!ops) return false;
    for (const op of ops) this.applyOp(op);
    this.undoStack.push(ops);
    this.rev++;
    return true;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
}

/** Builds an op batch, applying each op immediately so later ops can depend on state. */
export class Txn {
  ops: Op[] = [];
  constructor(private g: Graph) {}

  do(op: Op): void {
    this.g.applyOp(op);
    this.ops.push(op);
  }

  addNode(x: number, y: number): number {
    const i = this.g.newId();
    this.do({ t: "addNode", n: { i, x, y } });
    return i;
  }

  addEdge(a: number, b: number, c: number): number | null {
    if (a === b || this.g.hasEdgeBetween(a, b)) return null;
    const i = this.g.newId();
    this.do({ t: "addEdge", e: { i, a, b, c } });
    return i;
  }

  delEdge(edgeId: number): void {
    const e = this.g.edges.get(edgeId);
    if (e) this.do({ t: "delEdge", e: { ...e } });
  }

  setClass(edgeId: number, to: number): void {
    const e = this.g.edges.get(edgeId);
    if (e && e.c !== to) this.do({ t: "setClass", i: edgeId, from: e.c, to });
  }

  addFlag(x: number, y: number, text: string, shape?: FlagShape): number {
    const i = this.g.newId();
    this.do({ t: "addFlag", f: shape ? { i, x, y, text, shape } : { i, x, y, text } });
    return i;
  }

  delFlag(flagId: number): void {
    const f = this.g.flags.get(flagId);
    if (f) this.do({ t: "delFlag", f: { ...f } });
  }

  setFlagText(flagId: number, to: string): void {
    const f = this.g.flags.get(flagId);
    if (f && f.text !== to) this.do({ t: "setFlagText", i: flagId, from: f.text, to });
  }

  delNodeWithEdges(nodeId: number): void {
    for (const e of this.g.edgesOf(nodeId)) this.delEdge(e.i);
    const n = this.g.nodes.get(nodeId);
    if (n) this.do({ t: "delNode", n: { ...n } });
  }

  /** Remove nodes that ended up isolated (degree 0), except those listed. */
  pruneIsolated(candidates: number[], keep: Set<number> = new Set()): void {
    for (const i of candidates) {
      if (!keep.has(i) && this.g.nodes.has(i) && this.g.degree(i) === 0) {
        const n = this.g.nodes.get(i)!;
        this.do({ t: "delNode", n: { ...n } });
      }
    }
  }

  /** Split an edge at (x, y): insert a new node, keep class on both halves. Returns new node id. */
  splitEdge(edgeId: number, x: number, y: number): number {
    const e = this.g.edges.get(edgeId)!;
    const mid = this.addNode(x, y);
    this.delEdge(edgeId);
    this.addEdge(e.a, mid, e.c);
    this.addEdge(mid, e.b, e.c);
    return mid;
  }

  commit(): boolean {
    if (this.ops.length === 0) return false;
    this.g.commit(this.ops);
    return true;
  }

  /** Roll back everything applied so far without touching the undo stack. */
  abort(): void {
    for (let i = this.ops.length - 1; i >= 0; i--) {
      const op = this.ops[i];
      this.g.applyOp(
        op.t === "addNode" ? { t: "delNode", n: op.n }
        : op.t === "delNode" ? { t: "addNode", n: op.n }
        : op.t === "addEdge" ? { t: "delEdge", e: op.e }
        : op.t === "delEdge" ? { t: "addEdge", e: op.e }
        : op.t === "setClass" ? { t: "setClass", i: op.i, from: op.to, to: op.from }
        : op.t === "addFlag" ? { t: "delFlag", f: op.f }
        : op.t === "delFlag" ? { t: "addFlag", f: op.f }
        : op.t === "setFlagText" ? { t: "setFlagText", i: op.i, from: op.to, to: op.from }
        : { t: "moveNode", i: op.i, from: op.to, to: op.from },
      );
    }
    this.ops = [];
  }
}
